import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getApiBaseUrl } from "../../config";

const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const user = getState().user?.currentUser;
    if (user?.id) {
      headers.set("userid", user.id);
    }
    return headers;
  },
});

// Define our API service
export const notificationsApi = createApi({
  reducerPath: "notificationApi",
  baseQuery,
  tagTypes: ["Notification"],
  endpoints: (builder) => ({
    // Get all notifications
    getNotifications: builder.query({
      async queryFn(
        {
          pageSize = 100,
          nextToken = undefined,
          page = 1,
          is_read = undefined,
          target = undefined,
          category = undefined,
          createdFrom = undefined,
          createdTo = undefined,
        } = {},
        queryApi,
        extraOptions,
        baseQuery
      ) {
        const params = {
          pageSize,
          page,
          ...(nextToken ? { nextToken } : {}),
          ...(is_read !== undefined ? { is_read } : {}),
          ...(target ? { target } : {}),
          ...(category ? { category } : {}),
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTo ? { createdTo } : {}),
        };

        const result = await baseQuery({
          url: "notifications",
          params,
        });
        return result;
      },
      providesTags: ["Notification"],
    }),

    getPublicBroadcastNotifications: builder.query({
      async queryFn({ limit = 5 } = {}, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: "public/broadcasts",
          params: { limit },
        });
        return result;
      },
    }),

    // Get a single notification by ID
    getNotificationById: builder.query({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `notifications/${id}`,
        });

        return result;
      },
      providesTags: (result, error, id) => [
        { type: "Notification", id },
        "Notification",
      ],
    }),

    // Create a new notification
    createNotification: builder.mutation({
      query: (formData) => ({
        url: "notifications",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Notification"],
    }),

    // Mark notification as read
    markNotificationAsRead: builder.mutation({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `notifications/${id}/read`,
          method: "PATCH",
        });

        return result;
      },
      invalidatesTags: (result, error, id) => [
        { type: "Notification", id },
        "Notification",
      ],
    }),
    // Mark notification as read
    searchAndMarkNotificationAsRead: builder.mutation({
      async queryFn({ id, module }, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `notifications/${id}/search`,
          method: "PATCH",
          body: { module },
        });

        return result;
      },
      invalidatesTags: (result, error, id) => [
        { type: "Notification", id },
        "Notification",
      ],
    }),

    // Update a notification
    updateNotification: builder.mutation({
      async queryFn({ id, formData }, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `notifications/${id}`,
          method: "PUT",
          body: formData,
        });
        return result;
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Notification", id },
        "Notification",
      ],
    }),

    // Delete a notification
    deleteNotification: builder.mutation({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `notifications/${id}`,
          method: "DELETE",
        });

        return result;
      },

      invalidatesTags: ["Notification"],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetPublicBroadcastNotificationsQuery,
  useGetNotificationByIdQuery,
  useCreateNotificationMutation,
  useMarkNotificationAsReadMutation,
  useSearchAndMarkNotificationAsReadMutation,
  useUpdateNotificationMutation,
  useDeleteNotificationMutation,
} = notificationsApi;
